import { supabase } from './supabase';

// Web port of app/src/lib/audio.ts. MediaRecorder captures to whatever the
// browser offers — Safari on iOS lands audio/mp4, Chrome lands audio/webm.
// We persist the Blob to IndexedDB via outbox.ts; upload sets Content-Type
// from the Blob so Supabase serves it back correctly.

export type RecordingResult = {
  blob: Blob;
  mimeType: string;
  durationMs: number;
};

// Pick the best available MIME for cross-device playback. iOS Safari cannot
// decode webm, so if Android Chrome records webm the clip won't play on an
// iPhone. Prefer mp4/AAC first — modern Chrome/Edge support it, Safari
// requires it, and the resulting file plays everywhere. Fall back to webm
// only on browsers that don't support mp4 recording.
function preferredMime(): string | undefined {
  const candidates = [
    'audio/mp4;codecs=mp4a.40.2',
    'audio/mp4',
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
  ];
  for (const m of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(m)) {
      return m;
    }
  }
  return undefined; // let the browser pick
}

export type Recorder = {
  stream: MediaStream;
  mediaRecorder: MediaRecorder;
  chunks: Blob[];
  mimeType: string;
  startedAt: number;
};

export async function ensureMicPermission(): Promise<boolean> {
  try {
    // Requesting the stream is the permission prompt. We release it
    // immediately if the caller didn't mean to start recording.
    const s = await navigator.mediaDevices.getUserMedia({ audio: true });
    s.getTracks().forEach((t) => t.stop());
    return true;
  } catch {
    return false;
  }
}

export async function startRecording(): Promise<Recorder> {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  const mime = preferredMime();
  const mediaRecorder = mime
    ? new MediaRecorder(stream, { mimeType: mime })
    : new MediaRecorder(stream);
  const chunks: Blob[] = [];
  mediaRecorder.ondataavailable = (e) => {
    if (e.data && e.data.size > 0) chunks.push(e.data);
  };
  mediaRecorder.start();
  return {
    stream,
    mediaRecorder,
    chunks,
    mimeType: mediaRecorder.mimeType || mime || 'audio/webm',
    startedAt: Date.now(),
  };
}

export function stopRecording(rec: Recorder): Promise<RecordingResult> {
  return new Promise((resolve) => {
    rec.mediaRecorder.onstop = () => {
      const blob = new Blob(rec.chunks, { type: rec.mimeType });
      rec.stream.getTracks().forEach((t) => t.stop());
      resolve({
        blob,
        mimeType: rec.mimeType,
        durationMs: Date.now() - rec.startedAt,
      });
    };
    rec.mediaRecorder.stop();
  });
}

export function cancelRecording(rec: Recorder): void {
  try {
    rec.mediaRecorder.stop();
  } catch {}
  rec.stream.getTracks().forEach((t) => t.stop());
}

function extensionFor(mimeType: string): string {
  if (mimeType.includes('webm')) return 'webm';
  if (mimeType.includes('ogg')) return 'ogg';
  if (mimeType.includes('mp4') || mimeType.includes('m4a')) return 'm4a';
  if (mimeType.includes('wav')) return 'wav';
  return 'bin';
}

export async function uploadAudioBlob(
  blob: Blob,
  senderId: string,
  mimeType: string
): Promise<string> {
  const ext = extensionFor(mimeType);
  const path = `${senderId}/${Date.now()}.${ext}`;
  const { error } = await supabase.storage.from('audio').upload(path, blob, {
    contentType: mimeType,
    upsert: false,
  });
  if (error) throw error;
  return path;
}

export async function signedAudioUrl(path: string): Promise<string> {
  const { data, error } = await supabase.storage.from('audio').createSignedUrl(path, 60 * 60);
  if (error) throw error;
  return data.signedUrl;
}

// Playback uses a persistent HTMLAudioElement piped through an AudioContext
// via MediaElementAudioSourceNode. The <audio> element gives us progressive
// decode (needed because MediaRecorder produces MP4 with the moov atom at
// the END, which decodeAudioData can't handle). Routing its output through
// WebAudio's destination fixes a known iOS home-screen PWA bug where raw
// <audio>.play() resolves successfully but produces no audible output —
// "plays" into a null sink in standalone mode.
let sharedAudio: HTMLAudioElement | null = null;
let audioCtx: AudioContext | null = null;
let audioGain: GainNode | null = null;
let mediaSource: MediaElementAudioSourceNode | null = null;
let audioUnlocked = false;
let blobUrlInUse: string | null = null;
const unlockListeners = new Set<(unlocked: boolean) => void>();

function notifyUnlock() {
  for (const l of unlockListeners) l(audioUnlocked);
}

export function isAudioUnlocked(): boolean {
  return audioUnlocked;
}

export function onAudioUnlockChange(fn: (unlocked: boolean) => void): () => void {
  unlockListeners.add(fn);
  return () => unlockListeners.delete(fn);
}

function getSharedAudio(): HTMLAudioElement {
  if (!sharedAudio) {
    const a = document.createElement('audio');
    a.preload = 'auto';
    a.setAttribute('playsinline', '');
    a.setAttribute('webkit-playsinline', '');
    a.style.display = 'none';
    if (typeof document !== 'undefined' && document.body) {
      document.body.appendChild(a);
    }
    sharedAudio = a;
  }
  return sharedAudio;
}

// True when running as an installed PWA (iOS home-screen / Android installed
// app). In that mode iOS has a known bug where raw <audio>.play() produces
// no audible output — we route through AudioContext to fix it. In a regular
// browser tab the raw <audio> sink works and wiring it through WebAudio
// would actually _break_ things: createMediaElementSource silences the
// direct sink and redirects to the ctx, which is suspended until user
// gesture, so untapped pages play nothing.
const isStandalone =
  (typeof navigator !== 'undefined' &&
    (navigator as unknown as { standalone?: boolean }).standalone === true) ||
  (typeof window !== 'undefined' &&
    'matchMedia' in window &&
    window.matchMedia('(display-mode: standalone)').matches);

const isIOS =
  typeof navigator !== 'undefined' &&
  (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
    ((navigator.platform === 'MacIntel' || navigator.userAgent.includes('Macintosh')) &&
      navigator.maxTouchPoints > 1));

// The null-sink standalone playback bug is an iOS home-screen problem. Applying
// the same WebAudio routing to Android/Desktop PWAs adds failure modes without
// buying anything, so keep the workaround tightly scoped.
const needsStandaloneWorkaround = isStandalone && isIOS;

function ensureCtx(): AudioContext | null {
  if (!needsStandaloneWorkaround) return null;
  if (audioCtx) return audioCtx;
  try {
    const Ctor: typeof AudioContext =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    audioCtx = new Ctor();
    audioGain = audioCtx.createGain();
    audioGain.gain.value = 1;
    audioGain.connect(audioCtx.destination);
    return audioCtx;
  } catch {
    return null;
  }
}

// Called once the ctx is actually running. createMediaElementSource silences
// the element's direct sink and reroutes through the ctx — only safe to do
// AFTER a successful resume(). Idempotent.
function wireIfPossible(): void {
  if (mediaSource || !audioCtx || audioCtx.state !== 'running') return;
  try {
    const a = getSharedAudio();
    mediaSource = audioCtx.createMediaElementSource(a);
    mediaSource.connect(audioGain!);
  } catch (e) {
    console.warn('audio ctx wire failed', e);
  }
}

async function ensureCtxResumed(): Promise<void> {
  const c = ensureCtx();
  if (!c) return;
  if (c.state === 'suspended') {
    try {
      await c.resume();
    } catch {}
  }
  wireIfPossible();
}

export function stopAllPlayback(): void {
  if (!sharedAudio) return;
  try {
    sharedAudio.pause();
  } catch {}
}

function clearBlobUrl(): void {
  if (!blobUrlInUse) return;
  URL.revokeObjectURL(blobUrlInUse);
  blobUrlInUse = null;
}

const SILENT_WAV =
  'data:audio/wav;base64,UklGRiYAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQEAAACA';

async function primeAudio(): Promise<void> {
  if (audioUnlocked) return;
  await ensureCtxResumed();
  const a = getSharedAudio();
  try {
    clearBlobUrl();
    a.muted = true;
    a.src = SILENT_WAV;
    a.load();
    await a.play();
    a.pause();
    a.currentTime = 0;
    a.muted = false;
    if (!audioUnlocked) {
      audioUnlocked = true;
      notifyUnlock();
    }
  } catch {
    a.muted = false;
  }
}

async function playbackSrcFor(url: string): Promise<string> {
  if (!needsStandaloneWorkaround) return url;
  const res = await fetch(url, { mode: 'cors', cache: 'no-store' });
  if (!res.ok) throw new Error(`Audio fetch failed (${res.status})`);
  const blob = await res.blob();
  clearBlobUrl();
  blobUrlInUse = URL.createObjectURL(blob);
  return blobUrlInUse;
}

export async function playAudioFromPath(path: string, volume = 1): Promise<HTMLAudioElement> {
  const url = await signedAudioUrl(path);
  await ensureCtxResumed();
  const audio = getSharedAudio();
  try {
    audio.pause();
  } catch {}
  audio.muted = false;
  audio.src = await playbackSrcFor(url);
  if (audioGain) {
    audioGain.gain.value = Math.max(0, Math.min(1, volume));
  } else {
    audio.volume = Math.max(0, Math.min(1, volume));
  }
  audio.load();
  await audio.play();
  if (!audioUnlocked) {
    audioUnlocked = true;
    notifyUnlock();
  }
  return audio;
}

export async function unlockAudio(): Promise<boolean> {
  await primeAudio();
  return audioUnlocked;
}

let lifecycleBound = false;
export function startAudioLifecycle(): void {
  if (lifecycleBound) return;
  lifecycleBound = true;
  // Prime the shared element immediately (handles cases where it works
  // without a gesture), and again on every user gesture until it sticks.
  primeAudio();
  const unlock = () => {
    if (audioUnlocked) return;
    primeAudio();
  };
  // Capture-phase so we beat any stopPropagation. We keep the listeners bound
  // even after unlock succeeds — no perf cost, and self-heals if the element
  // re-locks (which iOS does after long idle periods).
  window.addEventListener('pointerdown', unlock, true);
  window.addEventListener('touchstart', unlock, true);
  window.addEventListener('keydown', unlock, true);
  window.addEventListener('click', unlock, true);

  // Pause playback when tab hides. Mirrors native's AppState-background stop.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') stopAllPlayback();
  });
  window.addEventListener('pagehide', stopAllPlayback);
}
